import React, { useState, useRef, useEffect } from 'react';
import '../../../../styles/resume-editor.css';

interface DateFormatProps {
  onDateFormatChange?: (settings: { date_format?: string }) => void;
  cvUid?: string;
  initialDateFormat?: string;
}

interface DateFormatOption {
  id: string;
  example: string;
  description: string;
}

export const DateFormat: React.FC<DateFormatProps> = ({ onDateFormatChange, cvUid, initialDateFormat }) => {
  const backendToFormatId: Record<string, string> = {
    '%b %y': 'jan-24-short',
    '%B %Y': 'january-2024',
    '%b %Y': 'jan-2024',
    '%m/%Y': '01-2024',
    '%Y': '2024',
  };

  const getInitialFormat = () => {
    if (initialDateFormat && backendToFormatId[initialDateFormat]) {
      return backendToFormatId[initialDateFormat];
    }
    return 'jan-24-short';
  };

  const [selectedFormat, setSelectedFormat] = useState<string>(getInitialFormat());
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const prevFormatRef = useRef<string>(getInitialFormat());

  useEffect(() => {
    if (initialDateFormat && backendToFormatId[initialDateFormat]) {
      const formatId = backendToFormatId[initialDateFormat];
      setSelectedFormat(formatId);
      prevFormatRef.current = formatId;
    }
  }, [initialDateFormat]);

  const formatIdToBackend: Record<string, string> = {
    'jan-24-short': '%b %y',
    'january-2024': '%B %Y',
    'jan-2024': '%b %Y',
    '01-2024': '%m/%Y',
    '2024': '%Y',
  };

  const dateFormats: DateFormatOption[] = [
    { id: 'jan-24-short', example: "Jan '24", description: 'Short Name & Year Abbreviated' },
    { id: 'january-2024', example: 'January 2024', description: 'Name & Year' },
    { id: 'jan-2024', example: 'Jan 2024', description: 'Short Name & Year' },
    { id: '01-2024', example: '01/2024', description: 'Number & Year' },
    { id: '2024', example: '2024', description: 'Year' },
  ];

  const selectedOption = dateFormats.find((format) => format.id === selectedFormat) || dateFormats[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="resume-editor__section-expanded-body">
      <div className="date-format__field">
        <label className="date-format__label">Date Format</label>
        <div className="date-format__dropdown" ref={dropdownRef}>
          <button
            type="button"
            className="date-format__dropdown-trigger"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <div className="date-format__dropdown-content">
              <span className="date-format__dropdown-example">{selectedOption.example}</span>
              <span className="date-format__dropdown-description">{selectedOption.description}</span>
            </div>
            <span className="date-format__dropdown-arrow">{dropdownOpen ? '▴' : '▾'}</span>
          </button>
          {dropdownOpen && (
            <div className="date-format__dropdown-menu">
              {dateFormats.map((format) => (
                <button
                  key={format.id}
                  type="button"
                  className={`date-format__dropdown-item ${selectedFormat === format.id ? 'date-format__dropdown-item--selected' : ''}`}
                  onClick={() => {
                    if (format.id !== prevFormatRef.current) {
                      setSelectedFormat(format.id);
                      prevFormatRef.current = format.id;
                      setDropdownOpen(false);
                      const backendFormat = formatIdToBackend[format.id];
                      if (backendFormat) {
                        onDateFormatChange?.({ date_format: backendFormat });
                      }
                    } else {
                      setDropdownOpen(false);
                    }
                  }}
                >
                  <span className="date-format__dropdown-item-example">{format.example}</span>
                  <span className="date-format__dropdown-item-description">{format.description}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

